import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LEGAL_VERSION } from "@/lib/legalVersion";

// Phase P.18 — one-shot modal shown after login when the tenant's stored
// `terms_version` differs from the current `LEGAL_VERSION`. Not shown for
// fresh signups (their terms_version is stamped at verify time).
//
// Mounted globally in `Layout.jsx` so it renders on every authenticated
// route without cluttering individual pages.
export default function LegalReacceptModal() {
  const { manager, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (!manager) return;
    // Only prompt if the tenant has a recorded acceptance and it's out-of-date.
    // If there's no stored acceptance at all (edge case for very old accounts)
    // we still prompt so the audit trail is populated.
    const stored = manager.terms_version || "";
    if (stored !== LEGAL_VERSION) {
      // Avoid re-triggering during the same browser session.
      const seen = sessionStorage.getItem("jbk:reaccept-dismissed");
      if (seen !== LEGAL_VERSION) setOpen(true);
    }
  }, [manager]);

  const accept = async () => {
    if (busy) return;
    if (!ack) { toast.error("Please confirm you've read the updates"); return; }
    setBusy(true);
    try {
      await api.post("/consent/reaccept");
      sessionStorage.setItem("jbk:reaccept-dismissed", LEGAL_VERSION);
      toast.success("Thanks — your acceptance was recorded.");
      setOpen(false);
      if (typeof refresh === "function") refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to record acceptance");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="legal-reaccept-modal"
    >
      <div className="bg-card border border-border rounded-[10px] max-w-md w-full p-5 space-y-4 shadow-2xl">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-accent">// Legal update</div>
          <h3 className="text-lg font-extrabold tracking-tight mt-1">
            We've updated our Terms &amp; Privacy Policy
          </h3>
        </div>
        <p className="text-sm text-foreground/80 leading-relaxed">
          Please review the latest versions and confirm to continue using JobsBook.app. Nothing in
          your workflow changes — this is a routine GDPR housekeeping step.
        </p>
        <ul className="text-sm text-foreground/85 leading-relaxed list-disc pl-5 space-y-1">
          <li>
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-accent underline" data-testid="reaccept-link-terms">
              Read the Terms of Service
            </Link>
          </li>
          <li>
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-accent underline" data-testid="reaccept-link-privacy">
              Read the Privacy Policy
            </Link>
          </li>
        </ul>
        <label className="flex items-start gap-2 text-sm text-foreground/85 cursor-pointer p-3 border border-border rounded-md">
          <input
            type="checkbox"
            className="mt-0.5 accent-accent"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            data-testid="reaccept-ack-checkbox"
          />
          <span>I have read and I accept the updated Terms of Service and Privacy Policy.</span>
        </label>
        <div className="flex justify-end">
          <Button
            onClick={accept}
            disabled={busy || !ack}
            className="btn-cta"
            data-testid="reaccept-confirm-btn"
          >
            {busy ? "Recording…" : "Accept and continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
